import type { Department } from "../contexts/AuthProvider";
import { type Job, NurseFullRole, type NurseRole } from "./models";
import { inverseRolesMap, rolesMap } from "./utilFunctions";

/**
 * Get all role groups that a single role belongs to
 * @param role - The nurse's role
 * @param chosenDepartment - Department to check role groups from
 * @returns Array of role group names that contain this role
 */
export const getRoleGroupsForRole = (role: NurseFullRole, chosenDepartment: Department): string[] => {
	if (!chosenDepartment.role_groups) return [];

	return Object.entries(chosenDepartment.role_groups).reduce((acc, group) => {
		const [groupName, groupRoles] = group;
		const formattedGroupRoles = groupRoles.map((r) => rolesMap[r as NurseRole]);
		if (formattedGroupRoles.includes(role)) {
			acc.push(groupName);
		}
		return acc;
	}, [] as string[]);
};

/**
 * Get the best matching role group name for a set of roles.
 * Tries exact match first, then finds a group containing all provided roles.
 * Falls back to "staff" if no group matches.
 */
export const getGroupNameForRoles = (roles: string[], chosenDepartment: Department): string => {
	if (!chosenDepartment.role_groups || roles.length === 0) return "staff";

	const roleShortCodes = new Set(roles.map((r) => inverseRolesMap[r as NurseFullRole] ?? r));

	for (const [groupName, groupRoles] of Object.entries(chosenDepartment.role_groups)) {
		const groupSet = new Set(groupRoles);
		if (roleShortCodes.size === groupSet.size && [...roleShortCodes].every((r) => groupSet.has(r))) {
			return groupName;
		}
	}

	for (const [groupName, groupRoles] of Object.entries(chosenDepartment.role_groups)) {
		const groupSet = new Set(groupRoles);
		if ([...roleShortCodes].every((r) => groupSet.has(r))) {
			return groupName;
		}
	}

	return "staff";
};

/**
 * The roles a pick of role groups stands for: every member of each picked group, each role once, in the department's
 * group order — what a post made by role group posts for (the classic post screen and the gaps post sheet alike).
 */
export const getRolesOfRoleGroups = (roleGroups: readonly string[], departmentRoleGroups: Department["role_groups"]): NurseFullRole[] => {
	const roles = Object.entries(departmentRoleGroups)
		.filter(([name]) => roleGroups.includes(name))
		.flatMap(([, members]) => members);
	// The department config stores roles by their short key, which is how the enum is keyed.
	return Array.from(new Set(roles)).map((short) => NurseFullRole[short as keyof typeof NurseFullRole]);
};

// ─── A request's roles ───────────────────────────────────────────────────────
// A request stores its roles as strings: full role names ("Registered Nurse (RN)"), occasionally the bare short code
// ("RN") — the backend's `resolve_job_for_request_role` reads either — or the marker "all". A job department posts by
// job with `roles: []`. The helpers below read a request's roles in all of those forms.

/** The value a request's `roles` holds when it is open to every role. */
const EVERY_ROLE = "all";

const FULL_ROLE_NAMES: ReadonlySet<string> = new Set(Object.values(NurseFullRole));

const isNurseFullRole = (role: string): role is NurseFullRole => FULL_ROLE_NAMES.has(role);

/** Both forms a request may store each of `roles` in, so a comparison against a request's roles finds either. */
export const storedFormsOfRoles = (roles: readonly NurseFullRole[]): string[] => roles.flatMap((role) => [role, inverseRolesMap[role]]);

/**
 * Whether a request's stored roles take staff of any of `roles`: a request marked "all" takes every role, whatever
 * `roles` holds; otherwise one of `roles` must be named as stored. The Requests screen's role-group filter reads a
 * request's roles this way, and so do the gaps view's request-to-section and add-candidate rules.
 */
export const requestTakesAnyRole = (requestRoles: readonly string[], roles: readonly string[]): boolean =>
	requestRoles.includes(EVERY_ROLE) || roles.some((role) => requestRoles.includes(role));

/**
 * The roles whose staff can take a posted request, out of `departmentRoles` (the department's roles plus every role a
 * nurse of it holds, `departmentNurseTypes`):
 * - all of them where the department schedules by job: the job is the request's dimension there — its posts carry
 *   `roles: []` — and any roles it does carry may be stale (the desktop posted-request matcher skips them for the same
 *   reason). The request's `job_id` cannot tell the two modes apart: it is never null (a role-mode post carries its
 *   role's job, or the "unknown" sentinel);
 * - all of them for a request marked "all", or naming no role;
 * - else the ones it names, in either stored form.
 */
export const rolesTakingRequest = (
	requestRoles: readonly string[],
	departmentRoles: readonly NurseFullRole[],
	jobManagementEnabled: boolean,
): NurseFullRole[] => {
	if (jobManagementEnabled) return [...departmentRoles];
	// DEFAULT: a role-mode request naming no role (the backend's example: a charge-only call-off replacement) restricts
	// no role, so every role may take it — reading `[]` literally offered nobody.
	if (requestRoles.length === 0) return [...departmentRoles];
	return departmentRoles.filter((role) => requestTakesAnyRole(requestRoles, storedFormsOfRoles([role])));
};

/**
 * The short role a request's shift-length options are keyed by — `department.full_shift_times` and
 * `partial_block_shifts` are keyed by short role (`resolveShiftTimeOptions`), a request's roles by full name. Its
 * first role's short key when that is a full role name; `undefined` for no role, "all" or any other value, so the
 * time editor keeps its own "default".
 */
export const requestShiftOptionsRole = (requestRoles: readonly string[]): NurseRole | undefined => {
	const [firstRole] = requestRoles;
	return firstRole !== undefined && isNurseFullRole(firstRole) ? inverseRolesMap[firstRole] : undefined;
};

export const getRoleGroups = (roles: NurseFullRole[], chosenDepartment: Department): string[] => {
	if (!chosenDepartment.role_groups) return [];

	return Object.entries(chosenDepartment.role_groups).reduce((acc, group) => {
		const [groupName, groupRoles] = group;
		const formattedGroupRoles = groupRoles.map((role) => rolesMap[role as NurseRole]);
		if (formattedGroupRoles.every((role) => roles.includes(role))) {
			acc.push(groupName);
		}
		return acc;
	}, [] as string[]);
};

export const getJobDisplayName = (jobId: string | undefined | null, chosenDepartment: Department, allJobs?: Job[]): string => {
	if (!jobId) return "";
	return chosenDepartment.jobs?.find((j) => j.id === jobId)?.displayName ?? allJobs?.find((j) => j.id === jobId)?.displayName ?? "";
};

const JOB_INITIALS_MAX = 5;

/**
 * Compact glyph for a job display name, for cells too narrow to hold the name itself (the full
 * name belongs in a tooltip alongside it).
 *
 * Multi-word names take each word's first letter ("Occupational Therapy Assistant" → OTA,
 * "Asst. Director of Nursing" → ADON); connector words are deliberately KEPT because dropping them
 * turns the familiar DON into "DN". Single-word names take their first letters instead
 * ("Nurse" → NURSE), since a lone "N" reads as nothing.
 */
export const getJobInitials = (displayName: string): string => {
	const words = displayName.trim().split(/\s+/).filter(Boolean);
	if (words.length === 0) return "";
	if (words.length === 1) return words[0].slice(0, JOB_INITIALS_MAX).toUpperCase();
	return words
		.slice(0, JOB_INITIALS_MAX)
		.map((w) => w[0])
		.join("")
		.toUpperCase();
};
